// Types only, so loading `client` loads neither package.
import type { AlpacaAccountIdentifier, AlpacaCredentials, AlpacaCredentialsProvider } from '@fleece/alpaca';
import type { AlpacaDataCredentials, AlpacaDataCredentialsProvider, PolygonApiKeyProvider } from '@fleece/marketdata';
import { AlpacaCredentialSummary, PolygonCredentialSummary } from '@fleece/models';
import { CredentialRequest, InternalServiceError, InvalidRequestError, LoggerFactory, NotFoundError } from '@fleece/utilities';
import { FleeceClient } from './fleece-client';

/*
 * The credential providers for a process with no route to the database: each asks a Fleece
 * service for its keys, keeps the answer, and asks again only when a refusal asks it to
 * refresh. Each is written out in full rather than sharing a helper, so one can change how
 * it chooses a key without the others following.
 */

const dataLogger = LoggerFactory.getLogger('RemoteAlpacaDataCredentialsProvider');
const accountLogger = LoggerFactory.getLogger('RemoteAlpacaCredentialsProvider');
const polygonLogger = LoggerFactory.getLogger('RemotePolygonApiKeyProvider');

export interface RemoteAlpacaDataCredentialsProviderProps {
  readonly client: Pick<FleeceClient, 'listCredentials' | 'getCredentialSecret'>;
}

/**
 * The keys market data is read with: the first credential the service holds that may serve
 * it, kept until a refusal asks for another.
 *
 * On a refresh it moves to a credential under another name, since the one refused would be
 * refused again. With only one, it asks for that one's keys again rather than failing: the
 * service may hold newer keys under the same name.
 */
export class RemoteAlpacaDataCredentialsProvider implements AlpacaDataCredentialsProvider {
  private last?: AlpacaDataCredentials;
  private lastName?: string;

  constructor(private readonly props: RemoteAlpacaDataCredentialsProviderProps) {}

  async resolve(request?: CredentialRequest): Promise<AlpacaDataCredentials> {
    if (this.last !== undefined && request?.refresh !== true) {
      return this.last;
    }
    const { client } = this.props;
    const { credentials } = await client.listCredentials();
    const candidates = credentials
      .map((listing) => listing.credential)
      .filter((credential): credential is AlpacaCredentialSummary => credential.kind === 'alpaca' && credential.supportData);
    const chosen = candidates.find((candidate) => candidate.name !== this.lastName) ?? candidates[0];
    if (chosen === undefined) {
      throw new InvalidRequestError(
        'The service holds no Alpaca credential that may serve market data. Add one in its console under Settings, or turn on "use for market data" on one it has.',
      );
    }
    const { credential } = await client.getCredentialSecret({ credentialId: chosen.credentialId });
    if (credential.kind !== 'alpaca') {
      throw new NotFoundError(`Credential "${chosen.name}" is no longer an Alpaca credential. Try again to use another.`);
    }
    if (this.lastName === undefined) {
      dataLogger.info(`Reading market data with credential "${credential.name}".`);
    } else if (credential.name === this.lastName) {
      dataLogger.warn(`Credential "${credential.name}" was refused and is the only one that may serve market data; using its keys as the service holds them now.`);
    } else {
      dataLogger.info(`Credential "${this.lastName}" was refused; reading market data with "${credential.name}" instead.`);
    }
    this.last = { apiKey: credential.accessKey, secretKey: credential.secretKey, live: credential.live };
    this.lastName = credential.name;
    return this.last;
  }
}

export interface RemoteAlpacaCredentialsProviderProps {
  readonly client: Pick<FleeceClient, 'listCredentials' | 'getCredentialSecret'>;
  readonly account: AlpacaAccountIdentifier;
}

/**
 * One Alpaca account's keys, for its trading and stream clients: the credential the service
 * holds for that account, kept until a refusal asks for it again.
 */
export class RemoteAlpacaCredentialsProvider implements AlpacaCredentialsProvider {
  private last?: AlpacaCredentials;

  constructor(private readonly props: RemoteAlpacaCredentialsProviderProps) {}

  async resolve(request?: CredentialRequest): Promise<AlpacaCredentials> {
    if (this.last !== undefined && request?.refresh !== true) {
      return this.last;
    }
    const { client, account } = this.props;
    const { accountId, live } = account;
    const { credentials } = await client.listCredentials();
    const found = credentials
      .map((listing) => listing.credential)
      .find((credential): credential is AlpacaCredentialSummary => credential.kind === 'alpaca' && credential.accountId === accountId);
    if (found === undefined) {
      throw new NotFoundError(`The service holds no credential for Alpaca account ${accountId}. Add its keys in the service's console under Settings.`);
    }
    if (found.live !== live) {
      // A client built for the other host would send these keys where they are refused, or
      // trade the wrong kind of money where they are not.
      throw new InternalServiceError(
        `Alpaca account ${accountId}'s credential is for a ${found.live ? 'live' : 'paper'} account, not the ${live ? 'live' : 'paper'} one this client was built for.`,
      );
    }
    const { credential } = await client.getCredentialSecret({ credentialId: found.credentialId });
    if (credential.kind !== 'alpaca') {
      throw new NotFoundError(`Credential "${found.name}" is no longer an Alpaca credential. Add Alpaca account ${accountId}'s keys in the service's console under Settings.`);
    }
    accountLogger.info(`${this.last === undefined ? 'Using' : 'Asked again for'} the keys of Alpaca account ${accountId}, from credential "${credential.name}".`);
    this.last = { accessKey: credential.accessKey, secretKey: credential.secretKey };
    return this.last;
  }
}

export interface RemotePolygonApiKeyProviderProps {
  readonly client: Pick<FleeceClient, 'listCredentials' | 'getCredentialSecret'>;
}

/**
 * The Polygon key: the first the service holds, kept until a refusal asks for another.
 *
 * On a refresh it moves to a credential under another name, since the key refused would be
 * refused again. With only one, it asks for that one's key again rather than failing: the
 * service may hold a newer key under the same name.
 */
export class RemotePolygonApiKeyProvider implements PolygonApiKeyProvider {
  private last?: string;
  private lastName?: string;

  constructor(private readonly props: RemotePolygonApiKeyProviderProps) {}

  async resolve(request?: CredentialRequest): Promise<string> {
    if (this.last !== undefined && request?.refresh !== true) {
      return this.last;
    }
    const { client } = this.props;
    const { credentials } = await client.listCredentials();
    const candidates = credentials.map((listing) => listing.credential).filter((credential): credential is PolygonCredentialSummary => credential.kind === 'polygon');
    const chosen = candidates.find((candidate) => candidate.name !== this.lastName) ?? candidates[0];
    if (chosen === undefined) {
      throw new InvalidRequestError('The service holds no Polygon key. Add one in its console under Settings.');
    }
    const { credential } = await client.getCredentialSecret({ credentialId: chosen.credentialId });
    if (credential.kind !== 'polygon') {
      throw new NotFoundError(`Credential "${chosen.name}" is no longer a Polygon credential. Try again to use another.`);
    }
    if (this.lastName === undefined) {
      polygonLogger.info(`Using the Polygon key of credential "${credential.name}".`);
    } else if (credential.name === this.lastName) {
      polygonLogger.warn(`Polygon credential "${credential.name}" was refused and is the only one; using its key as the service holds it now.`);
    } else {
      polygonLogger.info(`Polygon credential "${this.lastName}" was refused; using "${credential.name}" instead.`);
    }
    this.last = credential.apiKey;
    this.lastName = credential.name;
    return this.last;
  }
}
